import { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search, Plus, Filter, Settings2, X, Download, Loader2, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { type BulkAction } from '@/components/bulk-actions-bar';
import { useBulkActions } from '@/contexts/BulkActionsContext';

import { BulkChangeStatusDialog } from '@/components/bulk-change-status-dialog';
import { BulkChangeManagerDialog } from '@/components/bulk-change-manager-dialog';
import PageHeaderBlock from '@/registry/evo-shadcn/blocks/page-header';
import { ProjectCreationPanel } from '@/components/project-creation-panel';
import { ColumnConfiguratorDrawer, DEFAULT_COLUMNS, type ColumnDefinition } from '@/components/column-configurator-drawer';
import { ProjectFilterSheet, applyColumnFilters, countActiveFilters, getProjectColumnValue, type ColumnFilters, type FilterReferenceData } from '@/components/project-filter-sheet';
import { useProjectForm } from '@/providers/ProjectFormProvider';
import { ProjectsGrid } from '@/components/projects-grid';

import type { ColumnSort } from '@/components/ui/data-table-enhanced';
import { fetchProjects, fetchClients, fetchEmployees, fetchTasks, fetchMilestones, fetchResourceAllocations, updateProject, createProject, deleteProject, type ProjectFilterParams } from '@/lib/data-service';
import { toast } from 'sonner';
import { useErrorHandler } from '@/hooks/use-error-handler';
import type { Project, Client, Employee, ProjectHealth, Task, Milestone, ResourceAllocation } from '@/types';
import type { PaginationMetadata } from '@/types/pagination';

import { useUserPreferences } from '@/providers/UserPreferencesProvider';
import { usePagination } from '@/hooks/use-pagination';
import { useKeyboardPagination } from '@/hooks/use-keyboard-pagination';
import { useColumnPreferences } from '@/hooks/use-column-preferences';
import { usePersistedFilters } from '@/hooks/use-persisted-filters';
import { useProjectGroupBy, } from '@/hooks/use-project-group-by';
import { type GroupById } from '@/lib/project-group-by-utils';
import { ProjectGroupByFilter } from '@/components/project-group-by-filter';
import { ROUTES } from '@/routes';
import { useCompany } from '@/contexts/CompanyContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useAuth } from '@/providers/AuthProvider';
import { useProjectStatuses } from '@/contexts/ProjectStatusContext';
import { PROJECT_STATUS_LABELS } from '@/types';
import { groupProjects } from '@/lib/project-group-by-utils';
import { ProjectsGroupedView } from '@/components/projects-grouped-view';
import { exportProjectsToExcel } from '@/lib/export-projects-excel';
import { TemplateFormSheet } from '@/components/template-form-sheet';
import { useTemplates } from '@/contexts/TemplateContext';
import { useIntegrationSettings } from '@/hooks/use-integration-settings';
import type { CreateTemplateData } from '@/types/template.types';

/** Column-header sort IDs that map to server-side sortBy values so
 *  server-side pagination can be used instead of fetching every row. */
const COLUMN_SORT_TO_SERVER: Record<string, string> = {
  'project-code': 'code',
  'project-name': 'name',
  'health-indicator': 'health',
  'sales-budget': 'value',
  'budget': 'value',
};

/** Delay before a change to the search box triggers an API request. Chosen to
 *  exceed the typical gap between keystrokes so a whole search term collapses
 *  into a single request instead of firing one request per character. */
const SEARCH_DEBOUNCE_MS = 500;

interface ProjectsPageProps {
  onNavigateToProject?: (projectId: number) => void;
  /** When true, hides the page header (used when embedded in the Project Team homepage). */
  hideHeader?: boolean;
}

export function ProjectsPage({ onNavigateToProject, hideHeader }: ProjectsPageProps = {}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentCompany } = useCompany();
  const { selectableStatuses, statusLabels } = useProjectStatuses();
  const integrationSettings = useIntegrationSettings();
  const { hasMinimizedForm, formState, acknowledgeModalOpen, restoreForm } = useProjectForm();
  const { preferences } = useUserPreferences();
  const { canPerformAction, projectVisibility } = usePermissions();
  const { authenticatedUser } = useAuth();
  const tableRef = useRef<HTMLDivElement>(null);
  const { handleApiError } = useErrorHandler({ useToast: true });
  const { addTemplate } = useTemplates();



  // Pagination (don't persist to URL to keep URLs clean)
  const {
    currentPage,
    pageSize,
    setPage,
    setPageSize,
    resetPagination,
  } = usePagination({ defaultPageSize: 25, persistInUrl: false, storageKey: 'projects-list-page' });
  
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [pagination, setPagination] = useState<PaginationMetadata>({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    startIndex: 0,
    endIndex: 0,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [resourceAllocations, setResourceAllocations] = useState<ResourceAllocation[]>([]);
  
  // Persisted filters - will be restored when navigating back to this page
  const { filters, updateFilter, resetFilters } = usePersistedFilters({
    storageKey: 'projects-list-filters',
    defaultFilters: {
      searchTerm: '',
      statusFilter: '2' as string,
      healthFilter: 'all' as ProjectHealth | 'all',
      sortBy: 'health' as 'name' | 'health' | 'value' | 'completion',
    },
  });
  
  const { searchTerm, statusFilter, healthFilter, sortBy } = filters;

  // Reset search term when the page mounts (navigating back should start with a clean search)
  useEffect(() => {
    updateFilter('searchTerm', '');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed filters from URL params on mount — e.g. ?health=at-risk from dashboard deep-links
  useEffect(() => {
    const healthParam = searchParams.get('health');
    if (healthParam) {
      updateFilter('healthFilter', healthParam as ProjectHealth | 'all');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Check if any filters are active
  const hasActiveFilters = searchTerm !== '' || statusFilter !== '2' || healthFilter !== 'all';
  
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [columnConfigOpen, setColumnConfigOpen] = useState(false);
  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // ── Bulk selection & actions ───────────────────────────────────────
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
  const [bulkStatusDialogOpen, setBulkStatusDialogOpen] = useState(false);
  const [bulkManagerDialogOpen, setBulkManagerDialogOpen] = useState(false);
  const [saveAsTemplateOpen, setSaveAsTemplateOpen] = useState(false);
  const [saveAsTemplateProject, setSaveAsTemplateProject] = useState<Project | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogProject, setDeleteDialogProject] = useState<Project | null>(null);
  const [deleteHasActivity, setDeleteHasActivity] = useState(false);

  // Hoist bulk-actions bar state up to app-layout via context so it renders
  // completely outside all overflow/scroll containers.
  const { registerBulkActions } = useBulkActions();

  // Clear selection whenever the page/filters change so stale IDs are removed
  // We reset on the raw projects array reference changing.
  const prevProjectsRef = useRef<Project[]>([]);
  useEffect(() => {
    if (prevProjectsRef.current !== projects) {
      setSelectedProjectIds((prev) => {
        if (prev.size === 0) return prev; // already empty, no state update needed
        const currentIds = new Set(projects.map((p) => p.id));
        const next = new Set([...prev].filter((id) => currentIds.has(id)));
        return next.size === prev.size ? prev : next;
      });
      prevProjectsRef.current = projects;
    }
  }, [projects]);

  // Persist column filters in sessionStorage so they survive navigation
  const [columnFilters, setColumnFiltersState] = useState<ColumnFilters>(() => {
    try {
      const stored = sessionStorage.getItem('projects-column-filters');
      if (stored) return JSON.parse(stored) as ColumnFilters;
    } catch {
      // ignore parse errors
    }
    return {};
  });

  const setColumnFilters = useCallback((filtersOrUpdater: ColumnFilters | ((prev: ColumnFilters) => ColumnFilters)) => {
    setColumnFiltersState(prev => {
      const next = typeof filtersOrUpdater === 'function' ? filtersOrUpdater(prev) : filtersOrUpdater;
      try {
        sessionStorage.setItem('projects-column-filters', JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, []);

  // Multi-column sort state: ordered array where first entry = primary sort, second = secondary, etc.
  // Click cycles: none -> asc -> desc -> remove
  // Persisted in sessionStorage so that sort order survives navigation away and back.
  const SORT_STORAGE_KEY = 'projects-column-sorts';
  const DEFAULT_COLUMN_SORTS: ColumnSort[] = [{ columnId: 'project-code', direction: 'asc' }];
  const [columnSorts, setColumnSorts] = useState<ColumnSort[]>(() => {
    try {
      const stored = sessionStorage.getItem(SORT_STORAGE_KEY);
      if (stored) return JSON.parse(stored) as ColumnSort[];
    } catch {
      // ignore corrupted data
    }
    return DEFAULT_COLUMN_SORTS;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(columnSorts));
    } catch {
      // sessionStorage full or unavailable – non-critical
    }
  }, [columnSorts]);


  
  // Use column preferences hook for persistence (API in live mode, localStorage in demo mode)
  const { columns, updateColumns } = useColumnPreferences({
    storageKey: 'projects-column-preferences',
    defaultColumns: DEFAULT_COLUMNS,
  });

  // Group By state (persisted in sessionStorage)
  const { groupBy, setGroupBy } = useProjectGroupBy();

  // Load reference data when component mounts or company changes
  const loadReferenceData = async () => {
    try {
      const [clientsData, employeesData, tasksData, milestonesData, allocationsData] = await Promise.all([
        fetchClients('projects'),
        fetchEmployees('projects'),
        fetchTasks('projects'),
        fetchMilestones('projects'),
        fetchResourceAllocations('projects'),
      ]);
      setClients(clientsData);
      setEmployees(employeesData);
      setTasks(tasksData);
      setMilestones(milestonesData);
      setResourceAllocations(allocationsData);
    } catch (error) {
      handleApiError(error, '/api/reference-data', 'GET');
    }
  };

  useEffect(() => {
    loadReferenceData();
  }, [handleApiError, currentCompany?.id]);

  // Open modal when form should be restored
  useEffect(() => {
    if (formState?.shouldOpenModal) {
      setCreateModalOpen(true);
      acknowledgeModalOpen();
    }
  }, [formState?.shouldOpenModal, acknowledgeModalOpen]);

  // Debounced search term for API calls (to avoid too many requests while typing)
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  // Debounce search term. The delay must comfortably exceed the gap between
  // keystrokes for an average typist — otherwise each keystroke's timer elapses
  // before the next key is pressed and every character fires its own request.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Function to load/refresh projects with pagination and filters
  // When skipServerPagination is true, all projects are fetched so that
  // client-side column filters (e.g. Client Code) can operate on the full
  // dataset instead of only the current page.
  const loadProjects = async (
    page: number = currentPage, 
    size: number = pageSize,
    filters?: ProjectFilterParams,
    skipServerPagination: boolean = false
  ) => {
    // Show loading state for initial load, transition state for page changes
    if (projects.length === 0) {
      setLoading(true);
    } else {
      setIsTransitioning(true);
    }

    try {
      // Build filter params
      const filterParams: ProjectFilterParams = filters || {
        statusId: statusFilter !== 'all' ? Number(statusFilter) : undefined,
        health: healthFilter,
        search: debouncedSearchTerm,
        sortBy: sortBy,
        scope: projectVisibility === 'managed-allocated' ? 'allocated' : undefined,
      };

      // When column filters are active we need the full dataset for client-side
      // filtering, so omit pagination params from the server request.
      const paginationParams = skipServerPagination
        ? undefined
        : { page, pageSize: size };

      // Fetch projects with pagination and filter params
      const result = await fetchProjects('projects', paginationParams, filterParams);
      
      // Check if result is a paginated response
      if (result && typeof result === 'object' && 'data' in result && 'pagination' in result) {
        // Server returned paginated response
        setProjects(result.data);
        setPagination({
          currentPage: result.pagination.currentPage,
          pageSize: result.pagination.pageSize,
          totalItems: result.pagination.totalItems,
          totalPages: result.pagination.totalPages,
          hasNextPage: result.pagination.hasNextPage,
          hasPreviousPage: result.pagination.hasPreviousPage,
          startIndex: result.pagination.startIndex,
          endIndex: result.pagination.endIndex,
        });
      } else {
        // Fallback: result is an array (demo mode or non-paginated response)
        const projectsArray = Array.isArray(result) ? result : [];
        setProjects(projectsArray);
        
        // Calculate pagination client-side for demo mode
        const totalItems = projectsArray.length;
        const totalPages = Math.ceil(totalItems / size);
        const safePage = Math.max(1, Math.min(page, totalPages || 1));
        const startIndex = (safePage - 1) * size;
        const endIndex = Math.min(startIndex + size, totalItems);
        
        setPagination({
          currentPage: safePage,
          pageSize: size,
          totalItems,
          totalPages,
          hasNextPage: safePage < totalPages,
          hasPreviousPage: safePage > 1,
          startIndex,
          endIndex,
        });
      }
      setLoading(false);
    } catch (error) {
      handleApiError(error, '/api/projects', 'GET');
      setLoading(false);
    } finally {
      setIsTransitioning(false);
    }
  };

  // Stable key that changes only when the column filter values change.
  // Used as a useEffect dependency to re-fetch data when filters are applied/cleared.
  const columnFilterKey = useMemo(() => JSON.stringify(columnFilters), [columnFilters]);

  // Stable key that changes when the sorted column or its direction changes, so
  // toggling asc → desc on a server-mapped sort triggers a re-fetch.
  const columnSortKey = useMemo(() => JSON.stringify(columnSorts), [columnSorts]);

  // Load projects when page, pageSize, or filters change
  useEffect(() => {
    const hasColumnFilters = countActiveFilters(columnFilters) > 0;
    const hasGrouping = groupBy.length > 0;

    // When a single column sort maps to a server-side sort we can let the
    // server handle ordering + pagination instead of fetching every row.
    const mappedServerSort = columnSorts.length === 1
      ? COLUMN_SORT_TO_SERVER[columnSorts[0].columnId]
      : undefined;
    const hasUnmappableColumnSorts = columnSorts.length > 0 && !mappedServerSort;

    const needsFullDataset = hasColumnFilters || hasUnmappableColumnSorts || hasGrouping;

    loadProjects(currentPage, pageSize, {
      statusId: statusFilter !== 'all' ? Number(statusFilter) : undefined,
      health: healthFilter,
      search: debouncedSearchTerm,
      sortBy: mappedServerSort || sortBy,
      sortOrder: mappedServerSort ? columnSorts[0].direction : undefined,
      scope: projectVisibility === 'managed-allocated' ? 'allocated' : undefined,
    }, needsFullDataset);
  }, [currentPage, pageSize, statusFilter, healthFilter, debouncedSearchTerm, sortBy, currentCompany?.id, columnSortKey, groupBy.length, columnFilterKey]); // eslint-disable-line react-hooks/exhaustive-deps



  // Reset to page 1 when filters or column sorts change (but not when page/pageSize changes).
  // Seed the search baseline with '' (the value the mount effect resets searchTerm to) so the
  // on-mount search reset doesn't count as a filter change and clobber a restored page.
  const prevFiltersRef = useRef({ statusFilter, healthFilter, debouncedSearchTerm: '', sortBy, columnSortKey, columnFilterKey });
  // Skip the first invocation so a page restored from sessionStorage survives the initial mount.
  const isFirstFiltersRun = useRef(true);
  useEffect(() => {
    if (isFirstFiltersRun.current) {
      isFirstFiltersRun.current = false;
      return;
    }

    const prevFilters = prevFiltersRef.current;
    const filtersChanged = 
      prevFilters.statusFilter !== statusFilter ||
      prevFilters.healthFilter !== healthFilter ||
      prevFilters.debouncedSearchTerm !== debouncedSearchTerm ||
      prevFilters.sortBy !== sortBy ||
      prevFilters.columnSortKey !== columnSortKey ||
      prevFilters.columnFilterKey !== columnFilterKey;
    
    if (filtersChanged && currentPage !== 1) {
      resetPagination();
    }
    
    prevFiltersRef.current = { statusFilter, healthFilter, debouncedSearchTerm, sortBy, columnSortKey, columnFilterKey };
  }, [statusFilter, healthFilter, debouncedSearchTerm, sortBy, columnSortKey, currentPage, resetPagination, columnFilterKey]);



  // Keyboard shortcuts for pagination
  useKeyboardPagination({
    currentPage,
    totalPages: pagination.totalPages,
    onPageChange: setPage,
    enabled: !loading && !createModalOpen && !columnConfigOpen,
  });

  // Apply visibility permission — when 'managed-allocated', only show projects
  // the current user manages, is allocated to, or is a key person on (any role).
  const visibleProjects = useMemo(() => {
    if (projectVisibility === 'all') return projects;
    const userId = authenticatedUser?.id;
    if (!userId) return projects;
    const userIdStr = String(userId);
    const allocatedProjectIds = new Set(
      resourceAllocations
        .filter(a => String(a.employeeId) === userIdStr)
        .map(a => a.projectId)
    );
    // Key People grant visibility regardless of role (Project Manager, Project
    // Sponsor, Technical Lead, etc.). Live data supplies keyPeopleEmployeeIds as a
    // comma-separated string; demo/tour data carries full roleAssignments objects.
    const isKeyPerson = (p: Project) => {
      const fromIds = (p.keyPeopleEmployeeIds ?? '')
        .split(',')
        .some(id => id.trim() === userIdStr);
      const fromAssignments = (p.roleAssignments ?? [])
        .some(a => String(a.employeeId) === userIdStr);
      return fromIds || fromAssignments;
    };
    return projects.filter(p =>
      String(p.projectManagerId) === userIdStr || allocatedProjectIds.has(p.id) || isKeyPerson(p)
    );
  }, [projects, projectVisibility, authenticatedUser?.id, resourceAllocations]);

  // Get current page of projects — apply column filters and optional client-side sort.
  // When column sorts or column filters are active the full dataset is fetched so
  // sorting/filtering operates across all pages, not just the current server page.
  const paginatedProjects = useMemo(() => {
    const refData: FilterReferenceData = { clients, employees };
    const filtered = applyColumnFilters(visibleProjects, columnFilters, refData);

    if (columnSorts.length === 0) return filtered;

    // A single sort that maps to a server-side sort is already ordered globally by
    // SQL (including its collation) for the requested page — re-sorting it here
    // would diverge from the server order.
    if (columnSorts.length === 1 && COLUMN_SORT_TO_SERVER[columnSorts[0].columnId]) return filtered;

    return [...filtered].sort((a, b) => {
      for (const { columnId, direction } of columnSorts) {
        const aVal = getProjectColumnValue(columnId, a, refData);
        const bVal = getProjectColumnValue(columnId, b, refData);

        // Handle undefined values - push them to the end
        if (aVal === undefined && bVal === undefined) continue;
        if (aVal === undefined) return 1;
        if (bVal === undefined) return -1;

        let cmp = 0;
        if (typeof aVal === 'number' && typeof bVal === 'number') {
          cmp = aVal - bVal;
        } else {
          cmp = String(aVal).localeCompare(String(bVal), undefined, { sensitivity: 'base' });
        }

        if (cmp !== 0) {
          return direction === 'asc' ? cmp : -cmp;
        }
      }
      return 0;
    });
  }, [projects, columnFilters, columnSorts, clients, employees]);

  // Count active column filters for the badge on the Filter button
  const activeColumnFilterCount = useMemo(() => countActiveFilters(columnFilters), [columnFilters]);

  const hasClientSideProcessing = activeColumnFilterCount > 0 ||
    (columnSorts.length > 0 && !(columnSorts.length === 1 && COLUMN_SORT_TO_SERVER[columnSorts[0].columnId]));

  // When column filters or column sorts are active the full dataset is loaded and
  // filtering + sorting + pagination must happen client-side. Compute correct
  // pagination metadata from the processed results rather than using the server
  // pagination state.
  const effectivePagination = useMemo((): PaginationMetadata => {
    if (!hasClientSideProcessing) return pagination;

    const totalItems = paginatedProjects.length;
    const totalPages = Math.ceil(totalItems / pageSize) || 1;
    const safePage = Math.max(1, Math.min(currentPage, totalPages));
    const startIndex = (safePage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalItems);

    return {
      currentPage: safePage,
      pageSize,
      totalItems,
      totalPages,
      hasNextPage: safePage < totalPages,
      hasPreviousPage: safePage > 1,
      startIndex,
      endIndex,
    };
  }, [hasClientSideProcessing, pagination, paginatedProjects.length, pageSize, currentPage]);

  // Slice the processed results for the current page when client-side processing is active.
  const displayProjects = useMemo(() => {
    if (!hasClientSideProcessing) return paginatedProjects;
    return paginatedProjects.slice(
      effectivePagination.startIndex,
      effectivePagination.endIndex,
    );
  }, [hasClientSideProcessing, paginatedProjects, effectivePagination.startIndex, effectivePagination.endIndex]);

  // Get visible columns sorted by order (needed for ProjectFilterSheet)
  const visibleColumns = columns
    .filter(col => col.visible)
    .sort((a, b) => a.order - b.order);

  const handleSaveColumns = (newColumns: ColumnDefinition[]) => {
    updateColumns(newColumns);
  };

  const handleNewProjectClick = () => {
    if (hasMinimizedForm) {
      restoreForm();
    }
    setCreateModalOpen(true);
  };

  // Export the currently visible project list (all filtered/sorted rows) to Excel.
  // When grouping is active the grouped layout (with header bands + subtotal rows)
  // is passed through so the Excel file mirrors exactly what the user sees on screen.
  const handleExportExcel = async () => {
    if (isExporting) return;
    setIsExporting(true);
    try {
      const source = projects;
      const filtered = applyColumnFilters(source, columnFilters, { clients, employees });

      // Pass the pre-computed grouped structure when grouping is active so the
      // export utility can render group header bands and subtotal rows.
      const grouped = groupBy.length > 0 ? groupedProjects : undefined;

      await exportProjectsToExcel(filtered, columns, referenceData, statusLabels, grouped);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setIsExporting(false);
    }
  };

  // Reference data bundle for the grid component
  const referenceData = useMemo(() => ({
    clients,
    employees,
    tasks,
    milestones,
    resourceAllocations,
  }), [clients, employees, tasks, milestones, resourceAllocations]);

  // Grouped projects (computed from the full filtered dataset when group-by is active)
  const groupedProjects = useMemo(() => {
    if (groupBy.length === 0) return [];
    const filtered = applyColumnFilters(visibleProjects, columnFilters, { clients, employees });
    return groupProjects(filtered, groupBy, { clients, employees, statusLabels });
  }, [groupBy, visibleProjects, columnFilters, clients, employees, statusLabels]);

  // Derive available options for filter sheet MultiSelect fields.
  const filterAvailableOptions = useMemo(() => {
    const allProjects = projects;
    const opts: Record<string, { value: string; label: string }[]> = {};

    // Project Name
    const projectNames = [...new Set(allProjects.map(p => p.name).filter(Boolean))];
    if (projectNames.length > 0) {
      opts['project-name'] = projectNames.sort().map(n => ({ value: n, label: n }));
    }

    // Project Code
    const projectCodes = [...new Set(allProjects.map(p => p.code).filter((c): c is string => !!c))];
    if (projectCodes.length > 0) {
      opts['project-code'] = projectCodes.sort().map(c => ({ value: c, label: c }));
    }

    // Client Code
    const clientCodes = [...new Set(clients.map(c => c.code).filter((c): c is string => !!c))];
    if (clientCodes.length > 0) {
      opts['client-code'] = clientCodes.sort().map(c => ({ value: c, label: c }));
    }

    // Client Name
    const clientNames = [...new Set(clients.map(c => c.name).filter(Boolean))];
    if (clientNames.length > 0) {
      opts['client-name'] = clientNames.sort().map(n => ({ value: n, label: n }));
    }

    // Project Manager (from employees)
    const managerNames = [...new Set(employees.map(e => e.name).filter(Boolean))];
    if (managerNames.length > 0) {
      opts['project-manager'] = managerNames.sort().map(n => ({ value: n, label: n }));
    }

    // Account Manager (same pool as employees)
    if (managerNames.length > 0) {
      opts['account-manager'] = managerNames.sort().map(n => ({ value: n, label: n }));
    }

    // Client Contact (from clients)
    const contacts = [...new Set(clients.map(c => c.contactName).filter(Boolean))];
    if (contacts.length > 0) {
      opts['client-contact'] = contacts.sort().map(c => ({ value: c, label: c }));
    }

    return opts;
  }, [projects, clients, employees]);

  // ── Bulk action handlers ───────────────────────────────────────────

  const handleBulkAction = (action: BulkAction) => {
    if (action === 'change-status') setBulkStatusDialogOpen(true);
    else if (action === 'change-manager') setBulkManagerDialogOpen(true);
    else if (action === 'duplicate') handleBulkDuplicate();
    else if (action === 'save-as-template') handleBulkSaveAsTemplate();
    else if (action === 'delete') handleBulkDelete();
  };

  const handleBulkDelete = () => {
    const [id] = [...selectedProjectIds];
    const source = projects.find((p) => p.id === id);
    if (!source) return;
    const hasActivity =
      (source.actualHours ?? 0) > 0 ||
      (source.actualRevenue ?? 0) > 0 ||
      (source.budgetRevenue ?? 0) > 0 ||
      (source.budgetLabour ?? 0) > 0;
    setDeleteDialogProject(source);
    setDeleteHasActivity(hasActivity);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteDialogProject) return;
    try {
      await deleteProject(deleteDialogProject.id, deleteHasActivity);
      setProjects((prev) => prev.filter((p) => p.id !== deleteDialogProject.id));
      toast.success(`"${deleteDialogProject.name}" has been deleted`);
    } catch (error) {
      toast.error('Failed to delete project. Please try again.');
    } finally {
      setSelectedProjectIds(new Set());
      setDeleteDialogOpen(false);
      setDeleteDialogProject(null);
    }
  };

  const handleCloseInstead = () => {
    setDeleteDialogOpen(false);
    setDeleteDialogProject(null);
    setBulkStatusDialogOpen(true);
  };

  const handleBulkSaveAsTemplate = () => {
    const [id] = [...selectedProjectIds];
    const allProjects = projects;
    const source = allProjects.find((p) => p.id === id);
    if (!source) return;
    setSaveAsTemplateProject(source);
    setSaveAsTemplateOpen(true);
  };

  const handleSaveAsTemplateSave = (data: CreateTemplateData) => {
    addTemplate({ ...data, isFavorite: false });
    setSaveAsTemplateOpen(false);
    setSaveAsTemplateProject(null);
    setSelectedProjectIds(new Set());
  };

  // Sync selection into the app-layout context so the bar renders at the root
  // level — completely outside all overflow/scroll clipping.
  useEffect(() => {
    if (selectedProjectIds.size > 0) {
      registerBulkActions({
        selectedCount: selectedProjectIds.size,
        onAction: handleBulkAction,
        onClear: () => setSelectedProjectIds(new Set()),
      });
    } else {
      registerBulkActions(null);
    }
    // Unregister when the page unmounts
    return () => registerBulkActions(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectIds.size]);



  const handleBulkChangeStatus = async (statusId: number) => {
    const ids = [...selectedProjectIds];
    const allProjects = projects;

    // Optimistic update: update local state immediately
    // Cast via `as` to satisfy the narrow ProjectStatusId union type
    const updateLocal = (p: Project): Project =>
      selectedProjectIds.has(p.id) ? { ...p, statusId: statusId as Project['statusId'] } : p;
    setProjects((prev) => prev.map(updateLocal));


    try {
      await Promise.all(
        ids.map((id) => {
          const project = allProjects.find((p) => p.id === id);
          if (!project) return Promise.resolve();
          return updateProject(id, { id, statusId });
        }),
      );
      toast.success(
        `Status updated for ${ids.length} ${ids.length === 1 ? 'project' : 'projects'}`,
      );
      setSelectedProjectIds(new Set());
    } catch (err) {
      toast.error('Failed to update status for some projects');
      // Reload to get fresh data
      loadProjects(currentPage, pageSize);
    }
  };

  const handleBulkChangeManager = async (employeeId: number) => {
    const ids = [...selectedProjectIds];
    const allProjects = projects;

    // Optimistic update
    const updateLocal = (p: Project): Project =>
      selectedProjectIds.has(p.id) ? { ...p, projectManagerId: employeeId } : p;
    setProjects((prev) => prev.map(updateLocal));


    try {
      await Promise.all(
        ids.map((id) => {
          const project = allProjects.find((p) => p.id === id);
          if (!project) return Promise.resolve();
          return updateProject(id, { id, projectManagerId: employeeId });
        }),
      );
      toast.success(
        `Project Manager updated for ${ids.length} ${ids.length === 1 ? 'project' : 'projects'}`,
      );
      setSelectedProjectIds(new Set());
    } catch (err) {
      toast.error('Failed to update Project Manager for some projects');
      loadProjects(currentPage, pageSize);
    }
  };

  const generateUniqueProjectCode = (sourceCode: string): string => {
    const maxLength = integrationSettings.projectCodeMaxLength;
    const baseCode = sourceCode.replace(/-COPY$/, '');
    const allProjects = projects;
    const existingCodes = new Set(allProjects.map((p) => p.code).filter(Boolean));

    let suffix = 1;
    while (existingCodes.has(`${baseCode.slice(0, maxLength - `-${suffix}`.length)}-${suffix}`)) {
      suffix++;
    }
    return `${baseCode.slice(0, maxLength - `-${suffix}`.length)}-${suffix}`;
  };

  const handleBulkDuplicate = async () => {
    const [id] = [...selectedProjectIds];
    const allProjects = projects;
    const source = allProjects.find((p) => p.id === id);
    if (!source) return;

    try {
      await createProject({
        name: `${source.name} (Copy)`,
        projectCode: source.code ? generateUniqueProjectCode(source.code) : undefined,
        description: source.description,
        statusId: source.statusId,
        type: source.type,
        startDate: source.startDate ? new Date(source.startDate) : undefined,
        endDate: source.endDate ? new Date(source.endDate) : undefined,
        clients: source.clientIds,
        projectManager: source.projectManagerId ? String(source.projectManagerId) : undefined,
      });
      toast.success(`"${source.name}" duplicated successfully`);
      setSelectedProjectIds(new Set());
      // Refresh to show the new project
      loadProjects(currentPage, pageSize);
    } catch (err) {
      toast.error('Failed to duplicate project');
    }
  };

  // Row click handler
  const handleRowClick = (project: Project) => {
    if (onNavigateToProject) {
      onNavigateToProject(project.id);
    } else {
      navigate(ROUTES.PROJECT_DETAILS(project.id));
    }
  };

  return (
    <div className="space-y-6">
      {!hideHeader && (
        <div data-tour="projects-header">
          <PageHeaderBlock
            title="Projects"
            subtitle="Manage and monitor all your projects"
            size={20}
            showActions={true}
            actions={
              canPerformAction('create') ? (
                <Button 
                  onClick={handleNewProjectClick}
                  variant={hasMinimizedForm ? "info" : "default"}
                >
                  <Plus />
                  {hasMinimizedForm ? 'Resume Project' : 'New Project'}
                </Button>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Data Sensitivity Toggle moved to header bar */}

      {/* Projects Table */}
      <Card ref={tableRef} data-tour="projects-table">
        <CardContent className="p-0">
          {/* Filters */}
          <div className="flex gap-4 p-6 pb-4" data-tour="projects-search-bar">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by project code, name or client..."
                  value={searchTerm}
                  onChange={e => updateFilter('searchTerm', e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchTerm && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => updateFilter('searchTerm', '')}
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                  >
                    <X />
                  </Button>
                )}
              </div>
            </div>
            <Select value={statusFilter} onValueChange={(v) => updateFilter('statusFilter', v)}>
              <SelectTrigger className="w-auto">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {selectableStatuses.length > 0
                  ? selectableStatuses.map((s) => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))
                  : Object.entries(PROJECT_STATUS_LABELS).map(([id, label]) => (
                      <SelectItem key={id} value={id}>
                        {label}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
            <Select value={healthFilter} onValueChange={(v) => updateFilter('healthFilter', v as ProjectHealth | 'all')}>
              <SelectTrigger className="w-auto">
                <SelectValue placeholder="Health" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Health</SelectItem>
                <SelectItem value="at-risk">At Risk (Amber + Red)</SelectItem>
                <SelectItem value="green">Green</SelectItem>
                <SelectItem value="amber">Amber</SelectItem>
                <SelectItem value="red">Red</SelectItem>
              </SelectContent>
            </Select>
            <ProjectGroupByFilter
              groupBy={groupBy}
              onGroupByChange={setGroupBy}
            />
            {hasActiveFilters && (
              <Button
                variant="outline"
                onClick={resetFilters}
                className="text-muted-foreground"
              >
                <X />
                Clear Filters
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setFilterSheetOpen(true)}
              className="h-9 w-9 p-0 relative"
              aria-label="Filter"
            >
              <Filter />
              {activeColumnFilterCount > 0 && (
                <Badge className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] rounded-full px-1 text-[10px] font-semibold flex items-center justify-center pointer-events-none">
                  {activeColumnFilterCount}
                </Badge>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setColumnConfigOpen(true)}
              className="h-9 w-9 p-0"
              aria-label="Configure"
              data-tour="projects-columns-btn"
            >
              <Settings2 />
            </Button>
            <Button
              variant="outline"
              onClick={handleExportExcel}
              disabled={isExporting || loading}
              className="h-9 w-9 p-0"
              aria-label="Export to Excel"
              title="Export to Excel"
            >
              {isExporting ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Download />
              )}
            </Button>
          </div>

          {groupBy.length === 0 ? (
            <ProjectsGrid
              data={displayProjects}
              referenceData={referenceData}
              columnDefinitions={columns}
              columnSorts={columnSorts}
              onColumnSortChange={setColumnSorts}
              onRowClick={handleRowClick}
              loading={loading}
              isTransitioning={isTransitioning}
              pageSize={pageSize}
              hasActiveFilters={hasActiveFilters || activeColumnFilterCount > 0}
              pagination={!loading && effectivePagination.totalItems > 0 ? effectivePagination : undefined}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              selectedIds={selectedProjectIds}
              onSelectionChange={setSelectedProjectIds}
            />
          ) : (
            <ProjectsGroupedView
              groups={groupedProjects}
              groupBy={groupBy}
              referenceData={referenceData}
              columnDefinitions={columns}
              columnSorts={columnSorts}
              onColumnSortChange={setColumnSorts}
              onRowClick={handleRowClick}
              loading={loading}
            />
          )}
        </CardContent>
      </Card>

      {/* Project Creation Modal */}
      <ProjectCreationPanel
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        mode="create"
        onProjectCreated={loadProjects}
      />

      {/* Column Configurator Drawer */}
      <ColumnConfiguratorDrawer
        open={columnConfigOpen}
        onOpenChange={setColumnConfigOpen}
        columns={columns}
        onSave={handleSaveColumns}
        pageKey="projects-list"
      />

      {/* Project Filter Sheet */}
      <ProjectFilterSheet
        open={filterSheetOpen}
        onOpenChange={setFilterSheetOpen}
        visibleColumns={visibleColumns}
        filters={columnFilters}
        onApply={setColumnFilters}
        onClear={() => setColumnFilters({})}
        availableOptions={filterAvailableOptions}
      />

      {/* Bulk: Change Status */}
      <BulkChangeStatusDialog
        open={bulkStatusDialogOpen}
        onOpenChange={setBulkStatusDialogOpen}
        selectedCount={selectedProjectIds.size}
        onConfirm={handleBulkChangeStatus}
      />

      {/* Bulk: Change Project Manager */}
      <BulkChangeManagerDialog
        open={bulkManagerDialogOpen}
        onOpenChange={setBulkManagerDialogOpen}
        selectedCount={selectedProjectIds.size}
        employees={employees}
        onConfirm={handleBulkChangeManager}
      />

      {/* Bulk Actions Bar is rendered via BulkActionsContext in app-layout.tsx
          at the root DOM level, outside all overflow/scroll containers */}

      {/* Save as Template — opens pre-populated TemplateFormSheet for the selected project */}
      <TemplateFormSheet
        open={saveAsTemplateOpen}
        onOpenChange={(open) => {
          setSaveAsTemplateOpen(open);
          if (!open) setSaveAsTemplateProject(null);
        }}
        mode="create"
        template={saveAsTemplateProject ? {
          id: '',
          templateName: `${saveAsTemplateProject.name} (Template)`,
          templateDescription: `Created from project: ${saveAsTemplateProject.name}`,
          description: saveAsTemplateProject.description || '',
          type: saveAsTemplateProject.type || '',
          clients: saveAsTemplateProject.clientIds || '',
          isFavorite: false,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        } : undefined}
        onSave={handleSaveAsTemplateSave}
      />

      {/* Delete Project confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete &ldquo;{deleteDialogProject?.name}&rdquo;?
            </AlertDialogTitle>
            {deleteHasActivity ? (
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <div className="flex gap-3 rounded-md border border-warning-200 bg-warning-50 p-3 text-warning-800">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <div className="text-sm">
                      <p className="font-medium">This project has recorded activity.</p>
                      <p className="mt-1">Consider closing it instead — this preserves all history and prevents further work being logged, without permanently removing anything.</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    If you still want to delete, this cannot be undone.
                  </p>
                </div>
              </AlertDialogDescription>
            ) : (
              <AlertDialogDescription>
                This project has no recorded activity and will be permanently removed. This cannot be undone.
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className={deleteHasActivity ? 'flex-col-reverse gap-2 sm:flex-row' : ''}>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            {deleteHasActivity ? (
              <>
                <Button variant="destructive" onClick={handleConfirmDelete}>
                  Delete Anyway
                </Button>
                <Button onClick={handleCloseInstead}>
                  Close Project Instead
                </Button>
              </>
            ) : (
              <Button variant="destructive" onClick={handleConfirmDelete}>
                Delete Project
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
